import { useState, FC } from 'react';
import { AlertProps } from '@mui/lab';
import { FeedbackContext } from './FeedbackContext';
import { FeedbackComponent } from './FeedbackComponent';

export type FeedbackProps = {
  message?: string;
  callback?: Function;
  severity?: AlertProps['severity'];
};

/**
 * @summary Провайдер фидбэка, который оборачивает всё приложение. Это нужно для
 * того, чтобы можно было в любом месте легко и быстро воспользоваться фидбэком.
 */
export const FeedbackProvider: FC = ({ children }) => {
  const [feedbackIsVisible, setVisibility] = useState<boolean>(false);
  const [message, setMessage] = useState<FeedbackProps['message']>(undefined);
  const [severity, setSeverity] = useState<FeedbackProps['severity']>(undefined);
  const duration = 2000;

  /**
   * @summary Функция, вызывающая фидбэк.
   *
   * @param message - Сообщение, которое будет отображено внутри
   * фидбэка.
   * @param callback - Коллбэк, который сработает одновременно с тем,
   * как фидбэк начнёт скрываться.
   * @param severity - Тип фидбэка. Полностью повторяет свойство severity
   * у компонента [Alert]{@link https://material-ui.com/api/alert/#props} от material-ui.
   *
   * @example
   *  onSubmit={async ({ password }) => {
        await axios.put(`/user_backend/change_password/${props.userId}`, { password });
        showFeedback({
          message: t('Password changed'),
          severity: 'success',
          callback: logout,
        });
      }}
   */
  const showFeedback = ({ message, callback, severity }: FeedbackProps): void => {
    setVisibility(true);
    setMessage(message);
    setSeverity(severity);
    setTimeout(() => {
      setVisibility(false);
      callback && callback();
    }, duration);
  };

  return (
    <FeedbackContext.Provider value={{ showFeedback }}>
      {children}
      <FeedbackComponent
        feedbackIsVisible={feedbackIsVisible}
        message={message}
        severity={severity}
      />
    </FeedbackContext.Provider>
  );
};
